import { useEffect, useRef, useState } from "react";
import { fetchExchangeRate } from "../data/api/currenciesApi";
import { needsZohoExchangeRateFetch } from "../utils/documentExchangeRate";
import { gstDay } from "../utils/gstApplicability";

export type UseZohoDocumentExchangeRateArgs = {
  /** Zoho backend + document currency ≠ company currency. */
  enabled: boolean;
  currencyId?: string | null;
  /** Document date (any common format); normalized to YYYY-MM-DD. */
  fromDate?: string | null;
  /** Current stored / input rate — skip fetch when a real rate is present. */
  currentRate?: unknown;
  /** Called with Zoho's rate; must not mark the rate as user-set. */
  onRateFetched: (rate: number) => void;
};

export type UseZohoDocumentExchangeRateResult = {
  loading: boolean;
  error: string | null;
};

/**
 * Auto-fills exchange rate from Zoho Books when FX applies and the rate is
 * empty / null / 1. Does not call the API when a real rate is already set.
 */
export function useZohoDocumentExchangeRate(
  args: UseZohoDocumentExchangeRateArgs,
): UseZohoDocumentExchangeRateResult {
  const { enabled, currencyId, fromDate, currentRate, onRateFetched } = args;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onRateFetchedRef = useRef(onRateFetched);
  onRateFetchedRef.current = onRateFetched;

  const currencyKey = String(currencyId ?? "").trim();
  const fromDateKey = gstDay(fromDate) ?? "";
  const shouldFetch =
    enabled &&
    currencyKey !== "" &&
    fromDateKey !== "" &&
    needsZohoExchangeRateFetch(currentRate);

  useEffect(() => {
    if (!shouldFetch) {
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    const debounceMs = 300;
    const timer = window.setTimeout(() => {
      (async () => {
        setLoading(true);
        setError(null);
        try {
          const res = await fetchExchangeRate(
            currencyKey,
            fromDateKey,
            controller.signal,
          );
          if (cancelled) return;
          const rate = Number(res.data?.rate);
          if (!Number.isFinite(rate) || rate <= 0) {
            setError("Zoho returned an invalid exchange rate");
            return;
          }
          onRateFetchedRef.current(rate);
        } catch (e) {
          if (cancelled) return;
          if (e instanceof DOMException && e.name === "AbortError") return;
          setError(
            e instanceof Error ? e.message : "Failed to fetch exchange rate",
          );
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, debounceMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [shouldFetch, currencyKey, fromDateKey]);

  return { loading: shouldFetch ? loading : false, error };
}
